import type { FieldPolicy } from '@apollo/client/cache';
import { StoreObject } from '@apollo/client/utilities';
import { get } from 'lodash';

// Field policy for the paginated Query.listWorkflowEntities: pages are cached
// under one entry regardless of `cursor`, and fetchMore pages merge into it.
// Exported because the external app builds its own Apollo cache and needs the
// same policy for "load more" to update watched queries.
export function createListWorkflowEntitiesFieldPolicy(): FieldPolicy {
  return {
    keyArgs: [
      'input',
      ['workflowTemplateId', 'sortByComponents', 'filterByComponents', 'omniSearchQuery'],
    ],
    merge(existing, incoming, { readField, mergeObjects, args }) {
      // initial page (empty or absent cursor): replace cache rather than merge
      if (!args?.input?.cursor) {
        return incoming;
      }
      // paginated, go ahead and merge
      const merged = {
        ...incoming,
        entities: existing ? [...existing.entities] : [],
        // sparse list keyed by cardId — cards with no children are
        // omitted — so the incoming page alone would drop the counts
        // for cards loaded on earlier pages, and tree rows built from
        // those counts would lose their expand toggle
        inverseRelatedCardCounts: existing?.inverseRelatedCardCounts
          ? [...existing.inverseRelatedCardCounts]
          : [],
        refs: {
          __typename: 'EntityRefs',
          personRefs: existing ? [...(existing.refs.personRefs || [])] : [],
          workflowEntityRefs: existing ? [...(existing.refs.workflowEntityRefs || [])] : [],
          workflowTemplateRefs: existing ? [...(existing.refs.workflowTemplateRefs || [])] : [],
          recurringEntitiesScheduleRefs: existing
            ? [...(existing.refs.recurringEntitiesScheduleRefs || [])]
            : [],
        },
      };

      // dedupe and add/merge
      const entityDedupeAndAdd = (path: string, keyField = 'id') => {
        const indexes = {};
        if (existing) {
          const existingPath = get(existing, path);
          existingPath?.forEach((card: StoreObject, index: number) => {
            const id = readField<string>(keyField, card);
            if (id) {
              indexes[id] = index;
            }
          });
        }
        const incomingPath = get(incoming, path);
        const mergedPath = get(merged, path);
        incomingPath?.forEach((entity: StoreObject) => {
          const id = readField<string>(keyField, entity);
          if (id) {
            const index = indexes[id];
            if (typeof index === 'number') {
              mergedPath[index] = mergeObjects(mergedPath[index], entity);
            } else {
              indexes[id] = mergedPath.length;
              mergedPath.push(entity);
            }
          }
        });
      };
      entityDedupeAndAdd('entities');
      entityDedupeAndAdd('inverseRelatedCardCounts', 'cardId');
      entityDedupeAndAdd('refs.personRefs');
      entityDedupeAndAdd('refs.workflowEntityRefs');
      entityDedupeAndAdd('refs.workflowTemplateRefs');
      entityDedupeAndAdd('refs.recurringEntitiesScheduleRefs');
      return merged;
    },
  };
}
